<template>
    <div class="video-container" @dblclick="handleDblClick">
        <section class="videoPlayerErrorOverlay" v-show="failed">
            <b-icon-exclamation-diamond-fill />
            <strong>Ocorreu um erro ao reproduzir esse vídeo</strong>
            <button type="button" @click="retryMosaicPlayer">
                <b-icon-arrow-repeat /> Tentar novamente
            </button>
        </section>

        <TmVideo v-show="!failed" :key="mosaicVideoKey" ref="mosaic" :id="id" :frames="frames" :delay="delay" :interval="interval"
            :source="source" :height="height" :width="width" :realTime="sourceObject.realTime"
            @failed="(val) => failed = val" />

        <info-modal id="infoModal" :visible.sync="showInfoModal" :max-width="'100vw'" :max-height="'100vh'"
            @close="onCloseModal">
            <template #header>
                <div class="d-flex flex-column justify-content-between w-100">
                    <h4 class="w-100 d-flex">{{ $i18n.t(type) }}</h4>
                </div>
            </template>

            <template #body>
                <div class="modal-video-layout">
                    <div class="modal-video-area">
                        <section class="videoPlayerErrorOverlay" v-if="failed">
                            <b-icon-exclamation-diamond-fill />
                            <strong>Ocorreu um erro ao reproduzir esse vídeo</strong>
                            <button type="button" @click="retryStandalonePlayer">
                                <b-icon-arrow-repeat /> Tentar novamente
                            </button>
                        </section>

                        <div class="modal-video-container">
                            <TmVideo ref="standalone" :id="id" :frames="frames" :delay="delay" :interval="interval"
                                :source="source" :height="height" :width="width" :realTime="sourceObject.realTime" />
                        </div>
                    </div>

                    <aside v-if="sourceObject.ptzEnabled" class="ptz-sidebar">
                        <div class="ptz-header">
                            <h5>PTZ</h5>
                        </div>

                        <div class="ptz-body">
                            <div class="ptz-speed">
                                <div class="ptz-section-title">
                                    Velocidade
                                    <span>{{ ptzSpeed }}</span>
                                </div>

                                <input v-model.number="ptzSpeed" class="ptz-speed-range" type="range" :min="minSpeed"
                                    :max="maxSpeed" :step="1">
                            </div>

                            <div class="ptz-steps">
                                <div class="ptz-section-title">Passo horizontal</div>
                                <input v-model.number="panStep" class="ptz-number-input" type="number" min="0"
                                    step="0.01">

                                <div class="ptz-section-title">Passo vertical</div>
                                <input v-model.number="tiltStep" class="ptz-number-input" type="number" min="0"
                                    step="0.01">
                            </div>

                            <div class="ptz-section-title">Movimento</div>

                            <div class="ptz-pad-double">
                                <button class="ptz-btn ptz-btn-large ptz-up-2"
                                    @mousedown.prevent="startPtzPress('up', 'large')" @mouseup.prevent="stopPtzPress()"
                                    @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('up', 'large')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ▲▲
                                </button>

                                <button class="ptz-btn ptz-btn-large ptz-left-2"
                                    @mousedown.prevent="startPtzPress('left', 'large')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('left', 'large')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ◀◀
                                </button>

                                <button class="ptz-btn ptz-btn-stop" @click="sendPtzCommand('stop', 'normal', 'stop')">
                                    ●
                                </button>

                                <button class="ptz-btn ptz-btn-large ptz-right-2"
                                    @mousedown.prevent="startPtzPress('right', 'large')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('right', 'large')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ▶▶
                                </button>

                                <button class="ptz-btn ptz-btn-large ptz-down-2"
                                    @mousedown.prevent="startPtzPress('down', 'large')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('down', 'large')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ▼▼
                                </button>

                                <button class="ptz-btn ptz-btn-normal ptz-up-1"
                                    @mousedown.prevent="startPtzPress('up', 'normal')" @mouseup.prevent="stopPtzPress()"
                                    @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('up', 'normal')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ▲
                                </button>

                                <button class="ptz-btn ptz-btn-normal ptz-left-1"
                                    @mousedown.prevent="startPtzPress('left', 'normal')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('left', 'normal')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ◀
                                </button>

                                <button class="ptz-btn ptz-btn-normal ptz-right-1"
                                    @mousedown.prevent="startPtzPress('right', 'normal')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('right', 'normal')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ▶
                                </button>

                                <button class="ptz-btn ptz-btn-normal ptz-down-1"
                                    @mousedown.prevent="startPtzPress('down', 'normal')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('down', 'normal')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    ▼
                                </button>
                            </div>

                            <div class="ptz-zoom">
                                <div class="ptz-section-title">Zoom</div>

                                <button class="ptz-action-btn" @mousedown.prevent="startPtzPress('zoom_in', 'normal')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('zoom_in', 'normal')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    Zoom +
                                </button>

                                <button class="ptz-action-btn" @mousedown.prevent="startPtzPress('zoom_out', 'normal')"
                                    @mouseup.prevent="stopPtzPress()" @mouseleave.prevent="stopPtzPress()"
                                    @touchstart.prevent="startPtzPress('zoom_out', 'normal')"
                                    @touchend.prevent="stopPtzPress()" @touchcancel.prevent="stopPtzPress()">
                                    Zoom -
                                </button>
                            </div>

                            <div v-if="presets.length" class="ptz-presets">
                                <div class="ptz-section-title">Presets</div>

                                <button v-for="preset in presets" :key="preset.token" class="ptz-preset-btn"
                                    @click="onPreset(preset)">
                                    {{ preset.name }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            </template>

            <template #footer>
                <span />
            </template>
        </info-modal>
    </div>
</template>

<script>
import TmVideo from './TmVideo.vue';
import { useVideoStore } from "@/stores/video";
import { useDataStore } from "@/stores/data";

export default {
    components: {
        TmVideo,
    },

    props: {
        id: {
            type: Number,
            default: 1,
        },
        frames: {
            type: Number,
            default: 1,
        },
        delay: {
            type: Number,
            default: 0,
        },
        interval: {
            type: Number,
            default: 500,
        },
        sourceObject: {
            type: Object,
            default: () => ({})
        },
        source: {
            type: String,
            default: "",
        },
        height: {
            type: Number,
            required: false,
        },
        width: {
            type: Number,
            required: false,
            default: 0,
        },
        type: {
            type: String,
            required: false,
            default: '',
        },
        minSpeed: {
            type: Number,
            required: false,
            default: 1,
        },
        maxSpeed: {
            type: Number,
            required: false,
            default: 10,
        },
        initialSpeed: {
            type: Number,
            required: false,
            default: 5,
        },
        serialNumber: {
            type: String,
            required: false,
            default: 'PTZTeste01',
        },
        initialPanStep: {
            type: Number,
            required: false,
            default: 0.3,
        },
        initialTiltStep: {
            type: Number,
            required: false,
            default: 0.3,
        },
    },

    data() {
        return {
            failed: false,
            showInfoModal: false,
            mosaicVideoKey: 0,
            ptzSpeed: 5,
            panStep: 0.3,
            tiltStep: 0.3,
            zoomStep: 0.025,

            // Controle clique simples x clique segurado
            ptzPressTimer: null,
            ptzHoldThreshold: 300,
            ptzPressing: false,
            ptzContinuousActive: false,
            ptzPressAction: null,
            ptzPressStep: 'normal',

            service: null,
        };
    },

    mounted() {
        this.ptzSpeed = this.initialSpeed;
        this.panStep = this.initialPanStep;
        this.tiltStep = this.initialTiltStep;
        this.service = new this.$dataService();

        window.addEventListener('mouseup', this.handleGlobalPointerUp);
        window.addEventListener('touchend', this.handleGlobalPointerUp);
        window.addEventListener('touchcancel', this.handleGlobalPointerUp);
    },

    beforeDestroy() {
        this.clearPtzPressState(true);

        window.removeEventListener('mouseup', this.handleGlobalPointerUp);
        window.removeEventListener('touchend', this.handleGlobalPointerUp);
        window.removeEventListener('touchcancel', this.handleGlobalPointerUp);
    },

    computed: {
        presets() {
            const dataStore = useDataStore();
            const videoStore = useVideoStore();

            const devId = this.sourceObject.device?.id;
            if (!devId) return [];

            const presetConfig = videoStore.getPresetsByDeviceId(devId);
            if (!presetConfig?.id) return [];

            const presetData = dataStore.getById(presetConfig.id);
            if (!presetData?.value) return [];

            try {
                const parsed = typeof presetData.value === "string"
                    ? JSON.parse(presetData.value)
                    : presetData.value;

                if (!Array.isArray(parsed)) return [];

                return parsed.map(item => ({
                    token: item.token,
                    name: item.name || `Preset ${item.token}`,
                }));
            } catch (error) {
                console.error("Erro ao converter presets:", error);
                return [];
            }
        },
    },

    methods: {
        handleDblClick() {
            this.showInfoModal = true;
        },

        onCloseModal() {
            this.clearPtzPressState(true);
            this.mosaicVideoKey += 1;
        },

        retryMosaicPlayer() {
            console.log(this.$refs.mosaic);
            if (this.$refs.mosaic) {
                this.$refs.mosaic.retry();
            }
        },

        retryStandalonePlayer() {
            if (this.$refs.standalone) {
                this.$refs.standalone.retry();
            }
        },

        handleGlobalPointerUp() {
            this.stopPtzPress();
        },

        startPtzPress(action, step = 'normal') {
            this.clearPtzPressState(false);

            this.ptzPressing = true;
            this.ptzContinuousActive = false;
            this.ptzPressAction = action;
            this.ptzPressStep = step;

            this.ptzPressTimer = setTimeout(() => {
                if (!this.ptzPressing) return;

                this.ptzPressTimer = null;
                this.ptzContinuousActive = true;

                // Clique segurado: manda continuous UMA vez
                this.sendPtzCommand(action, step, 'continuous');
            }, this.ptzHoldThreshold);
        },

        stopPtzPress(sendCommand = true) {
            const action = this.ptzPressAction;
            const step = this.ptzPressStep;
            const wasPressing = this.ptzPressing;
            const wasContinuous = this.ptzContinuousActive;
            const wasSimpleClick = wasPressing && !wasContinuous;

            if (this.ptzPressTimer) {
                clearTimeout(this.ptzPressTimer);
                this.ptzPressTimer = null;
            }

            this.ptzPressing = false;
            this.ptzContinuousActive = false;
            this.ptzPressAction = null;
            this.ptzPressStep = 'normal';

            if (!sendCommand) return;

            if (wasContinuous) {
                // Soltou depois de continuous: manda stop
                this.sendPtzCommand('stop', 'normal', 'stop');
                return;
            }

            if (wasSimpleClick && action) {
                // Clique rápido: manda relative UMA vez
                this.sendPtzCommand(action, step, 'relative');
            }
        },

        clearPtzPressState(sendStop = true) {
            if (this.ptzPressTimer) {
                clearTimeout(this.ptzPressTimer);
                this.ptzPressTimer = null;
            }

            if (sendStop && this.ptzContinuousActive) {
                this.sendPtzCommand('stop', 'normal', 'stop');
            }

            this.ptzPressing = false;
            this.ptzContinuousActive = false;
            this.ptzPressAction = null;
            this.ptzPressStep = 'normal';
        },

        sendPtzCommand(action, step = 'normal', command = 'relative') {
            const sn = this.sourceObject?.device?.serialNumber || this.serialNumber;

            if (!sn) {
                console.warn("SerialNumber da câmera não encontrado para comando PTZ.");
                return;
            }

            if (!this.service || !this.service.sendPtzPosition) {
                console.warn("Serviço PTZ não inicializado.");
                return;
            }

            if (command === 'stop' || action === 'stop') {
                const data = {
                    command: 'stop',
                    pan: '0',
                    tilt: '0',
                    zoom: '0',
                    speed: '0',
                };

                this.service.sendPtzPosition(sn, data);
                return;
            }

            const multiplier = step === 'large' ? 2 : 1;
            const panValue = this.panStep * multiplier;
            const tiltValue = this.tiltStep * multiplier;
            const zoomValue = this.zoomStep * multiplier;

            const data = {
                command: command,
                pan: '0',
                tilt: '0',
                zoom: '0',
                speed: String(this.ptzSpeed / 10),
            };

            switch (action) {
                case 'up':
                    data.tilt = String(tiltValue);
                    break;

                case 'down':
                    data.tilt = String(-tiltValue);
                    break;

                case 'left':
                    data.pan = String(-panValue);
                    break;

                case 'right':
                    data.pan = String(panValue);
                    break;

                case 'zoom_in':
                    data.zoom = String(zoomValue);
                    break;

                case 'zoom_out':
                    data.zoom = String(-zoomValue);
                    break;

                default:
                    return;
            }

            this.service.sendPtzPosition(sn, data);
        },

        onPreset(preset) {
            const data = {
                gotoPreset: preset.token,
            };

            const sn = this.sourceObject?.device?.serialNumber || this.serialNumber;

            if (!sn) {
                console.warn("SerialNumber da câmera não encontrado para preset PTZ.");
                return;
            }

            if (!this.service || !this.service.sendPtzPreset) {
                console.warn("Serviço PTZ não inicializado.");
                return;
            }

            this.service.sendPtzPreset(sn, data);
        },
    },
};
</script>

<style>
.video-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
}

.modal-video-layout {
    width: 92vw;
    height: 88vh;
    display: flex;
    background: #000;
    overflow: hidden;
}

.modal-video-area {
    position: relative;
    flex: 1;
    min-width: 0;
    background: #000;
}

.modal-video-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
}

.ptz-sidebar {
    width: 340px;
    min-width: 340px;
    max-width: 340px;
    height: 100%;
    background: #111;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    color: #fff;
    padding: 1rem;
    overflow-y: auto;
}

.ptz-header {
    margin-bottom: 1rem;
}

.ptz-header h5 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
}

.ptz-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.ptz-section-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
}

.ptz-speed-range {
    width: 100%;
    cursor: pointer;
}

.ptz-pad-double {
    position: relative;
    width: 240px;
    height: 240px;
    margin: 0 auto;
}

.ptz-up-2,
.ptz-down-2,
.ptz-left-2,
.ptz-right-2,
.ptz-up-1,
.ptz-down-1,
.ptz-left-1,
.ptz-right-1,
.ptz-btn-stop {
    position: absolute;
}

.ptz-up-2 {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
}

.ptz-down-2 {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
}

.ptz-left-2 {
    left: 0;
    top: 50%;
    transform: translateY(-50%);
}

.ptz-right-2 {
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}

.ptz-up-1 {
    top: 52px;
    left: 50%;
    transform: translateX(-50%);
}

.ptz-down-1 {
    bottom: 52px;
    left: 50%;
    transform: translateX(-50%);
}

.ptz-left-1 {
    left: 52px;
    top: 50%;
    transform: translateY(-50%);
}

.ptz-right-1 {
    right: 52px;
    top: 50%;
    transform: translateY(-50%);
}

.ptz-btn-stop {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.ptz-btn {
    border: none;
    border-radius: 0.75rem;
    background: #1f1f1f;
    color: #fff;
    cursor: pointer;
    transition: 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    -webkit-user-select: none;
    touch-action: none;
}

.ptz-btn:hover {
    background: #2b2b2b;
}

.ptz-btn-large {
    width: 68px;
    height: 68px;
    font-size: 1.25rem;
    background: #252525;
}

.ptz-btn-normal {
    width: 56px;
    height: 56px;
    font-size: 1.1rem;
    background: #1b1b1b;
}

.ptz-btn-stop {
    width: 54px;
    height: 54px;
    font-size: 1rem;
    background: #7b1f2b;
}

.ptz-btn-stop:hover {
    background: #9d2a39;
}

.ptz-action-btn,
.ptz-preset-btn {
    width: 100%;
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    background: #1f1f1f;
    color: #fff;
    text-align: left;
    cursor: pointer;
    transition: 0.2s ease;
    user-select: none;
    -webkit-user-select: none;
    touch-action: none;
}

.ptz-action-btn:hover,
.ptz-preset-btn:hover {
    background: #2b2b2b;
}

.ptz-zoom,
.ptz-presets {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.video-element {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.video-js .vjs-tech {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    position: relative;
}

.video-js {
    width: 100% !important;
    height: 100% !important;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ptz-steps {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.ptz-number-input {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0.5rem;
    padding: 0.65rem 0.75rem;
    background: #1f1f1f;
    color: #fff;
    outline: none;
}

.ptz-number-input:focus {
    border-color: rgba(255, 255, 255, 0.28);
}
</style>